import { Link } from '@tanstack/react-router'
import {
  DPA,
  LEGAL_ENTITY,
  LEGAL_LAST_UPDATED,
  LEGAL_REVIEW_REQUIRED,
  PRIVACY,
  SUBPROCESSORS,
  TERMS,
  type LegalDocument,
} from '@shared/legal'
import { MarketingShell } from '../components/Layout'
import { Banner, Card } from '../ui'

/**
 * One renderer for all three legal documents.
 *
 * They share a shape — a lead, then numbered sections of prose — so they share a component;
 * three near-identical page files is how the wording in one drifts from the other two. The
 * subprocessor table is the single exception, injected into whichever section asked for it,
 * because it is the one piece a security reviewer will actually go looking for and a paragraph
 * listing six vendors in prose is unreadable.
 */
function LegalPage({ document }: { document: LegalDocument }) {
  return (
    <MarketingShell>
      <section className="container section stack gap-5" style={{ paddingBlock: '56px 24px' }}>
        <div className="stack gap-3" style={{ maxWidth: '65ch' }}>
          <span className="eyebrow">{document.eyebrow}</span>
          <h1 className="display-small">{document.title}</h1>
          <p className="body-large text-muted">{document.lead}</p>
          <p className="body-small text-muted">Last updated: {LEGAL_LAST_UPDATED}</p>
        </div>
        {/* Deliberately at the top and deliberately not styled down. Someone about to rely on
            this in a procurement pack needs to know before they read it, not after. */}
        <div style={{ maxWidth: '65ch' }}>
          <Banner tone="warn">{LEGAL_REVIEW_REQUIRED}</Banner>
        </div>
      </section>

      <section className="container" style={{ paddingBottom: '48px' }}>
        <div className="stack gap-4" style={{ maxWidth: '75ch' }}>
          {document.sections.map((section, index) => (
            <Card key={section.heading} variant="outlined">
              <div className="stack gap-3">
                <h2 className="title-large">
                  <span className="text-muted" style={{ marginRight: '10px' }}>
                    {index + 1}
                  </span>
                  {section.heading}
                </h2>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph.slice(0, 40)} className="body-medium text-muted">
                    {paragraph}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="stack gap-2" style={{ margin: 0, paddingLeft: '20px' }}>
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="body-medium text-muted">
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}
                {section.heading === 'Who else touches it' && <SubprocessorTable />}
              </div>
            </Card>
          ))}

          <Card variant="outlined">
            <div className="stack gap-2">
              <h2 className="title-large">Who we are</h2>
              <p className="body-medium text-muted">
                {LEGAL_ENTITY.name}, {LEGAL_ENTITY.address}. Questions about this document go to{' '}
                {LEGAL_ENTITY.contact}.
              </p>
              <div className="row gap-3 wrap" style={{ marginTop: '8px' }}>
                <Link to="/terms" className="body-small">
                  Terms of Service
                </Link>
                <Link to="/privacy" className="body-small">
                  Privacy Policy
                </Link>
                <Link to="/dpa" className="body-small">
                  Data Processing Addendum
                </Link>
                <Link to="/trust" className="body-small">
                  Trust &amp; transport
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </MarketingShell>
  )
}

function SubprocessorTable() {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="body-small" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '4px' }}>
        <thead>
          <tr>
            {['Who', 'What for', 'What they get'].map((h) => (
              <th
                key={h}
                style={{
                  textAlign: 'left',
                  padding: '8px 12px 8px 0',
                  borderBottom: '1px solid var(--outline-variant, #ddd)',
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SUBPROCESSORS.map((sub) => (
            <tr key={sub.name}>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top', fontWeight: 500 }}>
                {sub.name}
              </td>
              <td className="text-muted" style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                {sub.purpose}
              </td>
              <td className="text-muted" style={{ padding: '10px 0', verticalAlign: 'top' }}>
                {sub.data}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function Terms() {
  return <LegalPage document={TERMS} />
}

export function Privacy() {
  return <LegalPage document={PRIVACY} />
}

export function Dpa() {
  return <LegalPage document={DPA} />
}
